import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import BaseButton from "../../../../BaseComponents/BaseButton";
import BackButton from "../../../../BaseComponents/BackButton";
import StaffAssignPanel from "../../../../BaseComponents/StaffAssignPanel";
import { holidayLabel } from "../../../../Constant/HR_HolidayTemplate/holidayTemplate";
import { checkStatusCodeSuccess } from "../../../../Constant/common";
import { editManageStaff, manageStaffList } from "../../../../Api/HolidayTemplate";
import { useSelectedCenter } from "../../../CenterContext";
import { usePageLoader } from "../../../../_helper/loading/LoadingContext";
import { Calendar } from "react-feather";

const ManageStaff = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { selectedCenterPayload } = useSelectedCenter();
  const { setLoading } = usePageLoader();

  const [withHoliday, setWithHoliday] = useState([]);
  const [withoutHoliday, setWithoutHoliday] = useState([]);
  const [searchWith, setSearchWith] = useState("");
  const [searchWithout, setSearchWithout] = useState("");
  const [btnLoader, setBtnLoader] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    manageStaffList(templateId, {
      ...selectedCenterPayload,
      selectedStaffSearch: searchWith,
      unselectedStaffSearch: searchWithout,
    })
      .then((res) => {
        if (checkStatusCodeSuccess(res.statusCode)) {
          setWithHoliday(res?.data?.selectedStaff || []);
          setWithoutHoliday(res?.data?.unselectedStaff || []);
        } else {
          setWithHoliday([]);
          setWithoutHoliday([]);
          toast.error(res?.message);
        }
      })
      .catch(() => {
        setWithHoliday([]);
        setWithoutHoliday([]);
      })
      .finally(() => setLoading(false));
  }, [templateId, selectedCenterPayload, searchWith, searchWithout, setLoading]);

  useEffect(() => {
    const t = setTimeout(fetchData, 0);
    return () => clearTimeout(t);
  }, [fetchData]);

  const toggle = (staff, isWith) => {
    const setter = isWith ? setWithHoliday : setWithoutHoliday;
    setter((prev) => prev.map((s) => (s.id === staff.id ? { ...s, selected: !s.selected } : s)));
  };

  const checkAll = (isWith) => {
    const list = isWith ? withHoliday : withoutHoliday;
    const setter = isWith ? setWithHoliday : setWithoutHoliday;
    const allOn = list.every((s) => s.selected);
    setter((prev) => prev.map((s) => ({ ...s, selected: !allOn })));
  };

  const getIds = (list, sel) => list.filter((s) => s.selected === sel).map((s) => s.id);

  const handleSave = () => {
    setBtnLoader(true);
    editManageStaff(templateId, {
      authIds: [...getIds(withHoliday, true), ...getIds(withoutHoliday, true)],
      unassignedUserIds: [...getIds(withHoliday, false), ...getIds(withoutHoliday, false)],
    })
      .then((res) => {
        if (checkStatusCodeSuccess(res.statusCode)) {
          toast.success(res?.message);
          navigate("/holidayTemplate");
        } else toast.error(res?.message);
      })
      .catch((err) => toast.error(err?.response?.data?.message || err?.message))
      .finally(() => setBtnLoader(false));
  };

  const totalSelected = getIds(withHoliday, true).length + getIds(withoutHoliday, true).length;

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "rgba(245,158,11,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Calendar size={20} strokeWidth={1.75} style={{ color: "#f59e0b" }} />
          </div>
          <div>
            <h1
              style={{
                fontSize: "1.375rem",
                fontWeight: 800,
                margin: 0,
                color: "var(--text-primary)",
              }}
            >
              {holidayLabel.manageStaffList}
            </h1>
            <p
              style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}
            >
              Select staff members to assign to this holiday template
            </p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {totalSelected > 0 && (
            <span
              style={{
                padding: "5px 12px",
                borderRadius: 999,
                fontSize: "0.75rem",
                fontWeight: 700,
                background: "rgba(99,102,241,0.1)",
                color: "var(--brand-indigo)",
              }}
            >
              {totalSelected} selected
            </span>
          )}
          <BackButton onClick={() => navigate("/holidayTemplate")} />
        </div>
      </div>

      {/* Two-panel grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 16,
        }}
      >
        <StaffAssignPanel
          title="Staff With This Holiday Template"
          variant="with"
          items={withHoliday}
          searchValue={searchWith}
          onSearch={setSearchWith}
          onToggle={(s) => toggle(s, true)}
          onCheckAll={() => checkAll(true)}
          allChecked={withHoliday.length > 0 && withHoliday.every((s) => s.selected)}
          deactiveLabel={holidayLabel.Deactive}
        />
        <StaffAssignPanel
          title="Staff With Another Holiday Template"
          variant="without"
          items={withoutHoliday}
          searchValue={searchWithout}
          onSearch={setSearchWithout}
          onToggle={(s) => toggle(s, false)}
          onCheckAll={() => checkAll(false)}
          allChecked={withoutHoliday.length > 0 && withoutHoliday.every((s) => s.selected)}
          deactiveLabel={holidayLabel.Deactive}
        />
      </div>

      {/* Save */}
      <div style={{ display: "flex", justifyContent: "flex-end", paddingBottom: 8 }}>
        <BaseButton color="primary" onClick={handleSave} loader={btnLoader} disabled={btnLoader}>
          {holidayLabel.Save}
        </BaseButton>
      </div>
    </div>
  );
};

export default ManageStaff;
